import { Box, Text, HStack, Select, Button, createListCollection, ListCollection } from "@chakra-ui/react";
import { prefectures, genders, ageGroups } from "@/lib/utils";

export type FilterType = "gender" | "prefecture" | "age_group";

interface Filters {
  gender?: string;
  prefecture?: string;
  age_group?: string;
}

interface Props {
  filters: Filters;
  onFilterChange: (filterType: FilterType, value: string) => void;
  onReset: () => void;
}

interface FilterSelectProps {
  collection: ListCollection<{ label: string; value: string }>;
  value: string;
  onChange: (value: string) => void;
  isActive: boolean;
  width: { base: string; sm: string };
  maxH?: string;
}

function FilterSelect({ collection, value, onChange, isActive, width, maxH }: FilterSelectProps) {
  return (
    <Select.Root
      collection={collection}
      value={[value]}
      onValueChange={v => onChange(v.value[0] || "")}
      size="md"
      width={width}
    >
      <Select.Control>
        <Select.Trigger
          borderRadius="full"
          bg={isActive ? "blue.50" : "gray.50"}
          borderColor={isActive ? "blue.200" : "gray.200"}
          _hover={{ bg: isActive ? "blue.100" : "gray.100" }}
          px={4}
        >
          <Select.ValueText />
        </Select.Trigger>
        <Select.IndicatorGroup>
          <Select.Indicator />
        </Select.IndicatorGroup>
      </Select.Control>
      <Select.Positioner>
        <Select.Content borderRadius="xl" shadow="xl" p={1} maxH={maxH} overflowY={maxH ? "auto" : undefined}>
          {collection.items.map(item => (
            <Select.Item
              item={item}
              key={item.value}
              borderRadius="lg"
              _hover={{ bg: "blue.50" }}
              px={3}
              py={2}
            >
              {item.label}
              <Select.ItemIndicator />
            </Select.Item>
          ))}
        </Select.Content>
      </Select.Positioner>
    </Select.Root>
  );
}

const gendersList = createListCollection({
  items: [
    { label: "性別: すべて", value: "" },
    ...Object.entries(genders).map(([key, label]) => ({ label, value: key })),
  ],
});

const prefecturesList = createListCollection({
  items: [
    { label: "都道府県: すべて", value: "" },
    ...Object.entries(prefectures).map(([key, label]) => ({ label, value: key })),
  ],
});

const ageGroupsList = createListCollection({
  items: [
    { label: "年代: すべて", value: "" },
    ...Object.entries(ageGroups).map(([key, label]) => ({ label, value: key })),
  ],
});

export function RankingFilter({ filters, onFilterChange, onReset }: Props) {
  const hasActiveFilters = filters.gender || filters.prefecture || filters.age_group;

  return (
    <Box
      bg="white"
      rounded="xl"
      shadow="sm"
      p={4}
      border="1px solid"
      borderColor="gray.100"
    >
      <HStack justify="space-between" mb={3}>
        <Text fontSize="sm" fontWeight="medium" color="gray.600">
          絞り込み
        </Text>
        {hasActiveFilters && (
          <Button
            size="xs"
            variant="ghost"
            colorPalette="gray"
            onClick={onReset}
          >
            リセット
          </Button>
        )}
      </HStack>
      <HStack gap={3} flexWrap="wrap" flexDirection={{ base: "column", sm: "row" }} alignItems={{ base: "stretch", sm: "center" }}>
        <FilterSelect
          collection={gendersList}
          value={filters.gender || ""}
          onChange={value => onFilterChange("gender", value)}
          isActive={!!filters.gender}
          width={{ base: "full", sm: "150px" }}
        />
        <FilterSelect
          collection={prefecturesList}
          value={filters.prefecture || ""}
          onChange={value => onFilterChange("prefecture", value)}
          isActive={!!filters.prefecture}
          width={{ base: "full", sm: "170px" }}
          maxH="300px"
        />
        <FilterSelect
          collection={ageGroupsList}
          value={filters.age_group || ""}
          onChange={value => onFilterChange("age_group", value)}
          isActive={!!filters.age_group}
          width={{ base: "full", sm: "150px" }}
        />
      </HStack>
    </Box>
  );
}
